// Studio Editor — a photo studio edits the WHOLE photograph at a photo-lab print size (2R … A4 / Letter): one person, two people or a
// group, with a picked background (colour / gradient / pattern / scene), the dress for one or two people, and the same extra-
// instruction tools as the AI photo editor. A full-width page with two panels: the controls on the left, the photo on the right.
// Its photos live in their own table (StudioImage, ids like "s12") and show up in My Files under their own tab.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft, IconArrowRight, IconCoins, IconDeviceMobileMessage, IconCrown, IconPhoto, IconRotate, IconRuler2, IconShirt, IconSparkles, IconMessage2, IconWand, IconX } from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import CropStage from '../generate-image/CropStage';
import PrintModal from '../generate-image/PrintModal';
import CustomDownloadModal from '../generate-image/CustomDownloadModal';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import AiTemplateFeedbackModal, { IMAGE_FEEDBACK_REASONS } from '../../../components/common/AiTemplateFeedbackModal';
import LimitReachedModal from '../../../components/common/LimitReachedModal';
import LowCreditBanner from '../../../components/common/LowCreditBanner';
import QualityInfoDialog from '../../../components/common/QualityInfoDialog';
import { notifications } from '../../../lib/notifications';
import { useMediaQuery } from '../../../lib/hooks';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import imageGenerationService from '../../../services/imageGenerationService';
import studioEditorService from '../../../services/studioEditorService';
import usageService from '../../../services/usageService';
import { optimizeImage } from '../../../utils/optimizeImage';
import { sanitizePhotoPrompt } from '../../../utils/sanitize';
import { sniffPhotoFile } from '../../../utils/imageSniff';
import { buildPhotoDisplayName, nextPhotoSerial } from '../../../utils/photoFileName';
import { toBnDigits } from '../../../utils/bnFormat';
import { copyImageToClipboard } from '../../../utils/imageShare';
import { playSuccessChime } from '../../../utils/notificationSound.js';
import { ALL_DRESS_COLORS } from '../../../constants/dressColors';
import { BackgroundPicker, CustomPromptField, OrientationToggle, SizeDialog, SizeOptions } from './StudioControls';
import StudioDressSection, { useStudioDressOptions } from './StudioDressSection';
import StudioToolsPanel, { bulkToolsFromList, bulkToolsPayload } from './StudioToolsPanel';
import PhotoBoothInbox from '../../../components/common/PhotoBoothInbox';
import photoCollectorService from '../../../services/photoCollectorService';
import { registerPrivateResult } from '../../../utils/privateResults';
import { createPendingJobStore, makeThumbDataUrl } from '../../../utils/pendingGenJobs';
import StudioPhotoArea, { StudioRecentPhotos } from './StudioPhotoArea';
import useFilePaste, { isPastedImage } from '../tools/components/common/useFilePaste';
import { NEUTRAL_EDITS, bakePhotoEdits, isEdited, useFilterThumbs, usePhotoEditPreview } from '../../../utils/photoEdits';
import {
    BG_BLENDS, DEFAULT_DRESS, DEFAULT_SIZE, PEOPLE,
    KEEP_BG, applyDressExtra, hdAllowed, studioDressRequest, isKnownGarment, isStudioSize, isValidBg, parseDress, parseSize, sizeDims, sizeKey,
} from './studioConstants';

const PREFS_KEY = 'sohozkaj_studio_prefs';

// Generations that outlive the editor (hidden with ✕, or the page left / reloaded): the backend still finishes and saves them, so a
// marker per in-flight job lets the next visit show its processing card in the recent strip until the photo appears (as in the AI editor).
const pendingJobs = createPendingJobStore('sohozkaj_studio_pending_jobs');
const PENDING_JOB_TTL_MS = 240000;             // a restored job not in the history by then is shown as failed (the credit is refunded)
const PENDING_JOB_MAX_AGE_MS = 15 * 60 * 1000; // older markers are dropped silently
const idNum = (id) => parseInt(String(id ?? '').replace(/\D/g, ''), 10) || 0; // "s15" → 15
const WHITE = '#FFFFFF';
// Every visit starts with "don't change the background" — the user turns it off to pick a new one.
const DEFAULT_BG = KEEP_BG;

// ── Saved form choices (this browser only) ──────────────────────────────────────────────────────────────────────────────────
// Everything read back is validated: a stale or edited value falls back to its default instead of breaking the form.
function loadPrefs() {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { /* no saved choices */ }
    const dress = (d) => {
        const base = { ...DEFAULT_DRESS, ...(d && typeof d === 'object' ? d : {}) };
        const { type, color } = parseDress(base.value);
        const known = type === 'original' || (isKnownGarment(type) && ALL_DRESS_COLORS.some((c) => c.id === color));
        return known ? base : { ...base, value: 'original' };
    };
    return {
        size: isStudioSize(saved.size) && parseSize(saved.size) ? saved.size : DEFAULT_SIZE,
        people: PEOPLE.includes(saved.people) ? saved.people : 'single',
        bg: DEFAULT_BG, // never restored from the saved choices (see DEFAULT_BG)
        dress: dress(saved.dress), dress1: dress(saved.dress1), dress2: dress(saved.dress2),
        dressMen: dress(saved.dressMen), dressWomen: dress(saved.dressWomen), dressChildren: dress(saved.dressChildren),
        // Extra instructions = the Bulk Photo Edit tools, saved as their request list ("id" / "id:level").
        tools: bulkToolsFromList(Array.isArray(saved.bulkTools) ? saved.bulkTools : []),
    };
}

// Pixel size of an image file as the browser shows it (EXIF orientation applied), or null when it can't be decoded (e.g. HEIC).
const readImageSize = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

// The whole photo, kept in its own proportions, with the long edge capped (the crop's "use the whole photo" choice).
const wholePhotoFile = (file, maxEdge = 1280) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        const k = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * k);
        canvas.height = Math.round(img.naturalHeight * k);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
});

export default function StudioEditor() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const fmtNum = (n) => (isBn ? toBnDigits(n) : String(n));
    const { user } = useAuthStore();
    const { selectedOfficeSpace: rawOfficeSpace } = useOfficeSpaceStore();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    // Below `xl` the controls collapse to the slim rail + a floating panel, so the photo keeps its size on a laptop-width screen.
    const isCompact = useMediaQuery('(max-width: 1279px)');
    const [searchParams, setSearchParams] = useSearchParams();

    // Admins have no office space (their own credit); a stale selection from an earlier role must not reach the backend.
    const isAdmin = user?.role === 'admin';
    const selectedOfficeSpace = isAdmin ? null : rawOfficeSpace;
    const isOperator = !!(selectedOfficeSpace && selectedOfficeSpace.ownerId !== user?.id);

    // ── The edit settings (remembered in this browser) ───────────────────────────────────────────────────────────────────────
    const [prefs] = useState(loadPrefs);
    const [size, setSize] = useState(prefs.size);
    const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
    const orientTouchedRef = useRef(false); // true once portrait / landscape was picked by hand (then a new photo no longer flips it)
    const [section, setSection] = useState('size'); // the rail's open section
    // Below `xl` only the rail stays on screen; a rail tab opens its section as a floating panel beside it (this flag), and the same
    // tab, the ✕, Esc or a tap outside closes it. From `xl` the panel is always open.
    const [panelOpen, setPanelOpen] = useState(false);
    // Photo Booth (the customers' QR photo inbox): its rail section only exists while the booth is on for this office space.
    const [boothOn, setBoothOn] = useState(false);
    const [boothMarkedIds, setBoothMarkedIds] = useState([]);
    const [boothRefresh, setBoothRefresh] = useState(0);
    const boothPendingRef = useRef(null); // { id, isPrivate } of a booth photo on its way through the crop
    const boothPhotoRef = useRef(null);   // …and of the photo now in the editor (null for an uploaded one)
    const [people, setPeople] = useState(prefs.people);
    const [bg, setBg] = useState(prefs.bg);
    // Merge the picked background into the photo's own one: '' (replace it), 'seamless' or 'strong'. Not saved between visits, like bg.
    const [bgBlend, setBgBlend] = useState('');
    const blendSent = bg !== KEEP_BG && BG_BLENDS.includes(bgBlend) ? bgBlend : '';
    const [dressSingle, setDressSingle] = useState(prefs.dress);
    const [dressLeft, setDressLeft] = useState(prefs.dress1);
    const [dressRight, setDressRight] = useState(prefs.dress2);
    // A group: one dress each for all the men, all the women and all the children
    const [dressMen, setDressMen] = useState(prefs.dressMen);
    const [dressWomen, setDressWomen] = useState(prefs.dressWomen);
    const [dressChildren, setDressChildren] = useState(prefs.dressChildren);
    const [tools, setTools] = useState(prefs.tools);
    const [customPrompt, setCustomPrompt] = useState(''); // free text: never kept in the saved choices
    const skipSaveRef = useRef(false);      // a reopened edit must not overwrite the remembered choices

    useEffect(() => {
        if (skipSaveRef.current) { skipSaveRef.current = false; return; }
        try { localStorage.setItem(PREFS_KEY, JSON.stringify({ size, people, bg, dress: dressSingle, dress1: dressLeft, dress2: dressRight, dressMen, dressWomen, dressChildren, bulkTools: bulkToolsPayload(tools) })); } catch { /* storage unavailable */ }
    }, [size, people, bg, dressSingle, dressLeft, dressRight, dressMen, dressWomen, dressChildren, tools]);

    // ── What the account allows ──────────────────────────────────────────────────────────────────────────────────────────────
    // (the dress list and colours themselves live in the shared StudioDressSection)
    const { showSubColors } = useStudioDressOptions();

    // The dress picker works for one person (single), for each of two (dual), and for all the men / women / children of a group.
    const isChanged = (d) => parseDress(d.value).type !== 'original';
    const activeDresses = { single: [dressSingle], dual: [dressLeft, dressRight], group: [dressMen, dressWomen, dressChildren] }[people];
    const dresses = { single: dressSingle, left: dressLeft, right: dressRight, men: dressMen, women: dressWomen, children: dressChildren };
    const setDressFor = { single: setDressSingle, left: setDressLeft, right: setDressRight, men: setDressMen, women: setDressWomen, children: setDressChildren };
    const anyDressChosen = activeDresses.some(isChanged);

    // ── Credits ──────────────────────────────────────────────────────────────────────────────────────────────────────────────
    const [usageStats, setUsageStats] = useState(null);
    const [tierCosts, setTierCosts] = useState(null);
    const [noCreditOpen, setNoCreditOpen] = useState(false);
    const refreshUsage = useCallback(() => {
        usageService.getUserUsageStats(selectedOfficeSpace?.id).then(setUsageStats).catch(() => { /* best effort */ });
    }, [selectedOfficeSpace?.id]);
    useEffect(() => { refreshUsage(); }, [refreshUsage]);
    useEffect(() => {
        usageService.getCreditCosts().then((r) => setTierCosts((r?.data || r)?.imageTiers || null)).catch(() => { /* the buttons work without prices */ });
    }, []);
    const effectiveCredit = usageStats?.credit ?? user?.credit ?? 0;

    // ── The photo ────────────────────────────────────────────────────────────────────────────────────────────────────────────
    const [file, setFile] = useState(null);             // the cropped photo that is sent
    const [previewUrl, setPreviewUrl] = useState(null);
    const [originalFile, setOriginalFile] = useState(null); // what was picked, kept for re-cropping
    const [cropSrc, setCropSrc] = useState(null);
    const [pendingCropFile, setPendingCropFile] = useState(null);
    const cropStageRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const setPhoto = (nextFile, original) => {
        setFile(nextFile);
        setOriginalFile(original || nextFile);
        setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return nextFile ? URL.createObjectURL(nextFile) : null; });
    };

    const commitFile = async (picked, original) => {
        setPhoto(await optimizeImage(picked, { maxDimension: 1600, targetBytes: 900 * 1024 }), original);
        setGenerated(null);
        boothPhotoRef.current = boothPendingRef.current; // null unless this photo came from the booth
        boothPendingRef.current = null;
    };

    // Turn the print to the photo's orientation (portrait / landscape), unless it was picked by hand since the last new photo.
    const followOrientation = async (img) => {
        if (orientTouchedRef.current) return;
        const dims = await readImageSize(img);
        if (!dims || dims.w === dims.h) return;
        setSize((cur) => {
            const p = parseSize(cur);
            return p && (dims.w > dims.h) !== p.landscape ? sizeKey(p.id, dims.w > dims.h) : cur;
        });
    };

    const processFile = async (selected) => {
        // A drop while a photo is being edited must not swap the photo under the running edit.
        if (!selected || isGenerating) return;
        boothPendingRef.current = null; // an uploaded / dropped photo is not a booth photo (the booth pick sets it again right after)
        const claimed = selected.type || '';
        const heicClaim = /\.(heic|heif)$/i.test(selected.name) || claimed === 'image/heic' || claimed === 'image/heif';
        // The browser's type is trusted only when it names a normal image (and the cropper still has to decode it). An empty / generic type
        // (phone file managers report that for HEIC) or a ".heic" name proves nothing, so those are judged by the file's own bytes.
        const vouched = claimed.startsWith('image/') && claimed !== 'image/svg+xml' && !heicClaim;
        if (selected.size > 100 * 1024 * 1024) {
            notifications.show({ title: t('imageGeneration.notifications.fileTooLarge'), message: t('imageGeneration.notifications.fileTooLargeMessage'), color: 'red' });
            return;
        }
        const sniffed = vouched ? 'image' : await sniffPhotoFile(selected).catch(() => null);
        if (!sniffed) {
            notifications.show({ title: t('imageGeneration.notifications.invalidFileType'), message: t('imageGeneration.notifications.invalidFileMessage'), color: 'red' });
            return;
        }
        // Every new photo sets the print orientation (a landscape photo → the landscape print); the crop then fine-tunes it.
        orientTouchedRef.current = false;
        await followOrientation(selected);
        let forCrop = selected;
        if (sniffed === 'heic') {
            try {
                const heic2any = (await import('heic2any')).default;
                const converted = await heic2any({ blob: selected, toType: 'image/jpeg', quality: 0.92 });
                const blob = Array.isArray(converted) ? converted[0] : converted;
                forCrop = new File([blob], selected.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
            } catch {
                // Not converted = nothing the editor (or the server) can read: say so instead of sending the raw file.
                notifications.show({ title: t('imageGeneration.notifications.fileReadError'), message: t('imageGeneration.notifications.fileReadErrorMessage'), color: 'red' });
                return;
            }
        }
        const reader = new FileReader();
        reader.onload = (e) => { setCropSrc(e.target.result); setPendingCropFile(forCrop); };
        reader.onerror = () => notifications.show({ title: t('imageGeneration.notifications.fileReadError'), message: t('imageGeneration.notifications.fileReadErrorMessage'), color: 'red' });
        reader.readAsDataURL(forCrop);
    };

    const closeCrop = () => { setCropSrc(null); setPendingCropFile(null); };
    // The crop is free by default, so the shape actually cropped decides portrait / landscape.
    const onCropApply = (cropped) => { const original = pendingCropFile; closeCrop(); commitFile(cropped, original); followOrientation(cropped); };
    const onCropSkip = async () => { const original = pendingCropFile; closeCrop(); if (original) commitFile(await wholePhotoFile(original), original); };
    const onCropError = () => {
        // If the browser cannot even draw it, it is not a photo we can edit — nothing is kept or sent.
        notifications.show({ title: t('imageGeneration.notifications.fileReadError'), message: t('imageGeneration.notifications.fileReadErrorMessage'), color: 'red' });
        closeCrop();
    };

    // The crop box has the shape of the chosen print; its output is big enough to keep the detail.
    const dims = sizeDims(size) || { w: 2, h: 3 };
    const cropRatio = dims.w / dims.h;
    const cropOut = cropRatio <= 1 ? { w: Math.round(1024 * cropRatio), h: 1024 } : { w: 1024, h: Math.round(1024 / cropRatio) };

    const dragHandlers = {
        onDragOver: (e) => { e.preventDefault(); setIsDragging(true); },
        onDragLeave: (e) => { e.preventDefault(); setIsDragging(false); },
        onDrop: (e) => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files?.[0]); },
    };

    // Ctrl/Cmd+U opens the picker while no photo is loaded.
    useEffect(() => {
        const onKey = (e) => {
            if (!((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u')) return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            if (file || cropSrc) return;
            e.preventDefault();
            fileInputRef.current?.click();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [file, cropSrc]);

    // Ctrl/Cmd+V pastes a copied image the same way, while no photo is loaded (like the AI photo editor): it goes through
    // processFile's checks and the crop, as an upload does.
    useFilePaste((files) => processFile(files[0]), { enabled: !file && !cropSrc, accept: isPastedImage });

    // ── Generating ───────────────────────────────────────────────────────────────────────────────────────────────────────────
    const [generated, setGenerated] = useState(null);     // { id, aiImage, ... } of the current result
    const [showResult, setShowResult] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [qualityInfoTier, setQualityInfoTier] = useState(null);
    // The settings the current result was made with — a new generation needs a change (it costs credit), like the AI photo editor.
    const settingsKey = useMemo(
        () => JSON.stringify([size, people, bg, blendSent, activeDresses, tools, customPrompt.trim()]),
        [size, people, bg, blendSent, dressSingle, dressLeft, dressRight, dressMen, dressWomen, dressChildren, tools, customPrompt], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const [generatedKey, setGeneratedKey] = useState(null);
    // A reopened edit adopts its settings as "what the result was made with". It is keyed on a counter (not on the settings changing):
    // reopening an edit whose settings equal the form's must still count, or the next real change would be swallowed.
    const [restoreSeq, setRestoreSeq] = useState(0);
    useEffect(() => { if (restoreSeq > 0) setGeneratedKey(settingsKey); }, [restoreSeq]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Recent studio photos ─────────────────────────────────────────────────────────────────────────────────────────────────
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const loadHistory = useCallback(async (page = 1, append = false) => {
        if (!isAdmin && !selectedOfficeSpace) { setHistory([]); return; }
        setHistoryLoading(true);
        try {
            const res = await imageGenerationService.getHistory({ page, limit: 20, source: 'studio', ...(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {}) });
            const items = res?.data?.history || [];
            const pg = res?.data?.pagination;
            setHistory((prev) => (append ? [...prev, ...items.filter((i) => !prev.some((p) => p.id === i.id))] : items));
            setHistoryPage(page);
            setHasMoreHistory(pg ? pg.page < pg.totalPages : items.length === 20);
        } catch { /* the strip just stays empty */ } finally { setHistoryLoading(false); }
    }, [isAdmin, selectedOfficeSpace?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadHistory(1, false); }, [loadHistory]);

    // ── Background generations (processing cards in the recent strip) ────────────────────────────────────────────────────────
    const [bgJobs, setBgJobs] = useState([]);
    const genTokenRef = useRef(0);               // id of every generation
    const foregroundTokenRef = useRef(null);     // the one shown in the main photo area
    const hiddenTokensRef = useRef(new Set());   // the ones pushed to the strip
    const jobsRef = useRef({});                  // token → { uid, file, original, thumb } (to bring a job back)
    const restoredJobsLoadedRef = useRef(false);

    // Jobs still running when the editor was left / reloaded come back as (non-clickable) processing cards.
    useEffect(() => {
        if (!(selectedOfficeSpace || isAdmin) || restoredJobsLoadedRef.current) return;
        restoredJobsLoadedRef.current = true;
        const scopeId = selectedOfficeSpace?.id ?? null;
        const now = Date.now();
        const all = pendingJobs.read();
        all.filter((j) => now - j.startedAt >= PENDING_JOB_MAX_AGE_MS).forEach((j) => pendingJobs.remove(j.uid));
        const mine = all.filter((j) => (j.officeSpaceId ?? null) === scopeId && now - j.startedAt < PENDING_JOB_MAX_AGE_MS);
        if (!mine.length) return;
        setBgJobs((prev) => [
            ...mine.map((j) => ({ token: j.uid, uid: j.uid, previewUrl: j.thumb || null, status: 'processing', restored: true, startedAt: j.startedAt, baselineMaxId: j.baselineMaxId || 0 })),
            ...prev,
        ]);
    }, [selectedOfficeSpace, isAdmin]);

    // While restored jobs wait: refresh the history every 4s (the reconcile below picks their photos up); time out the ones that never come.
    useEffect(() => {
        if (!bgJobs.some((j) => j.restored && j.status === 'processing')) return undefined;
        const iv = setInterval(() => {
            const now = Date.now();
            setBgJobs((prev) => prev.map((j) => {
                if (j.restored && j.status === 'processing' && now - j.startedAt > PENDING_JOB_TTL_MS) { pendingJobs.remove(j.uid); return { ...j, status: 'error' }; }
                return j;
            }));
            loadHistory(1, false);
        }, 4000);
        return () => clearInterval(iv);
    }, [bgJobs]); // eslint-disable-line react-hooks/exhaustive-deps

    // A restored job is done once a studio photo newer than the newest one at its start shows up (oldest job ↔ oldest new photo).
    useEffect(() => {
        const waiting = bgJobs.filter((j) => j.restored && j.status === 'processing').sort((a, b) => a.startedAt - b.startedAt);
        if (!waiting.length || !history.length) return;
        const claimed = new Set();
        const resolved = [];
        waiting.forEach((job) => {
            const match = history.filter((h) => idNum(h.id) > job.baselineMaxId && !claimed.has(h.id)).sort((a, b) => idNum(a.id) - idNum(b.id))[0];
            if (match) { claimed.add(match.id); resolved.push(job.uid); }
        });
        if (!resolved.length) return;
        setBgJobs((prev) => prev.filter((j) => !resolved.includes(j.uid)));
        resolved.forEach((uid) => pendingJobs.remove(uid));
        playSuccessChime();
        refreshUsage();
        notifications.show({ title: t('imageGeneration.notifications.success'), message: t('imageGeneration.notifications.backgroundImageReady'), color: 'green' });
    }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGenerate = async (quality = 'hd') => {
        if (!file || isGenerating) return;
        if (!isAdmin && !selectedOfficeSpace) {
            notifications.show({ title: t('common.error'), message: t('officeSpace.noWorkspaceSelected'), color: 'red' });
            return;
        }
        // Not enough credit (the advance allowance included) → the same prompt as the AI photo editor, before anything is sent.
        const advance = usageStats?.advanceCredits ?? 0;
        const cost = tierCosts?.[quality] ?? usageStats?.advanceCost ?? 1; // the HD / Ultra HD price when known
        if (effectiveCredit - cost < -advance) { setNoCreditOpen(true); return; }

        const formData = new FormData();
        formData.append('image', file);
        formData.append('size', size);
        formData.append('people', people);
        // Dress: one garment (single), or several joined with | — left|right for two people, men|women|children for a group.
        const D = studioDressRequest(people, dresses, showSubColors);
        formData.append('dress', D.dress);
        if (people !== 'single' || D.dressColor) formData.append('dressColor', D.dressColor);
        if (people !== 'single' || D.dressColorExtra) formData.append('dressColorExtra', D.dressColorExtra);
        formData.append('dressCode', D.dressCode);
        formData.append('bg', isValidBg(bg) ? bg : WHITE);
        if (blendSent) formData.append('bgBlend', blendSent);
        const prompt = sanitizePhotoPrompt(customPrompt).trim();
        if (prompt) formData.append('userCustomInstruction', prompt);
        const bulkList = bulkToolsPayload(tools);
        if (bulkList.length) formData.append('bulkTools', JSON.stringify(bulkList));
        formData.append('quality', quality);
        if (selectedOfficeSpace?.id) formData.append('officeSpaceId', selectedOfficeSpace.id);
        // Name: "<shop> - <date> - <serial>", the serial counting on from the last studio photo of the day.
        const shopName = (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || '').trim() || 'Sohozkaj';
        const settings = user?.settings?.photoNameSettings;
        formData.append('displayName', buildPhotoDisplayName({ shopName, settings, serial: nextPhotoSerial({ shopName, settings, existingNames: history.map((h) => h.fileName) }) }));

        const keyAtStart = settingsKey;
        const boothAtStart = boothPhotoRef.current;
        // This generation's id + a marker that survives leaving the page (cleared when the request settles here).
        const token = ++genTokenRef.current;
        foregroundTokenRef.current = token;
        const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        jobsRef.current[token] = { uid, file, original: originalFile, thumb: null };
        pendingJobs.add({ uid, thumb: null, startedAt: Date.now(), officeSpaceId: selectedOfficeSpace?.id ?? null, baselineMaxId: history.reduce((m, h) => Math.max(m, idNum(h.id)), 0) });
        makeThumbDataUrl(previewUrl).then((thumb) => {
            if (!thumb) return;
            pendingJobs.update(uid, { thumb });
            if (jobsRef.current[token]) jobsRef.current[token].thumb = thumb;
            setBgJobs((prev) => prev.map((j) => (j.token === token && !j.previewUrl ? { ...j, previewUrl: thumb } : j)));
        });
        const isForeground = () => foregroundTokenRef.current === token && !hiddenTokensRef.current.has(token);

        setIsGenerating(true);
        try {
            const res = await studioEditorService.generate(formData);
            if (!res?.data) throw new Error('empty');
            // A booth photo is used up by a successful edit: it leaves the queue. A PRIVATE one makes every result from it private too
            // (deleted after its first download / print — utils/privateResults.js), regenerations included.
            if (boothAtStart) {
                if (boothAtStart.isPrivate && res.data.id != null) registerPrivateResult(res.data.id);
                if (boothAtStart.id != null) {
                    photoCollectorService.removeUpload(boothAtStart.id).catch(() => {}).finally(() => setBoothRefresh((k) => k + 1));
                    if (boothPhotoRef.current === boothAtStart) boothPhotoRef.current = { ...boothAtStart, id: null };
                }
            }
            if (!isForeground()) {
                // Finished while hidden: the photo lands in the strip, its processing card goes.
                hiddenTokensRef.current.delete(token);
                playSuccessChime();
                refreshUsage();
                loadHistory(1, false).finally(() => setBgJobs((prev) => prev.filter((j) => j.token !== token)));
                notifications.show({ title: t('imageGeneration.notifications.success'), message: t('imageGeneration.notifications.backgroundImageReady'), color: 'green' });
                return;
            }
            setGenerated(res.data);
            setGeneratedKey(keyAtStart);
            setShowResult(true);
            playSuccessChime();
            refreshUsage();
            loadHistory(1, false);
            notifications.show({ title: t('imageGeneration.notifications.success'), message: t('imageGeneration.notifications.imageGeneratedSuccess'), color: 'green' });
        } catch (error) {
            if (!isForeground()) {
                hiddenTokensRef.current.delete(token);
                setBgJobs((prev) => prev.map((j) => (j.token === token ? { ...j, status: 'error' } : j)));
                notifications.show({ title: t('imageGeneration.notifications.error'), message: t('imageGeneration.notifications.backgroundImageFailed'), color: 'red' });
                return;
            }
            if (error?.code === 'INSUFFICIENT_CREDITS') { setNoCreditOpen(true); return; }
            const msg = error?.message || '';
            const friendly = error?.code === 'STUDIO_HD_SIZE' ? t('studioEditor.hdSizeBlocked')
                : msg.includes('too large') || msg.includes('413') ? t('imageGeneration.notifications.fileTooLargeError')
                : msg === 'Network Error' || msg.includes('network') ? t('imageGeneration.notifications.networkError')
                    : t('imageGeneration.notifications.imageGenerationError');
            notifications.show({ title: t('imageGeneration.notifications.error'), message: friendly, color: 'red' });
        } finally {
            pendingJobs.remove(uid);
            delete jobsRef.current[token];
            // Only the job in front owns the spinner: a hidden one finishing must not stop whatever is generating now.
            if (foregroundTokenRef.current === token) {
                setIsGenerating(false);
                foregroundTokenRef.current = null;
            }
        }
    };

    // ✕ on a running edit: it keeps going, its processing card moves to the recent strip and the editor is free for the next photo.
    const hideCurrentGeneration = () => {
        const token = foregroundTokenRef.current;
        if (token == null || !isGenerating) return;
        hiddenTokensRef.current.add(token);
        const job = jobsRef.current[token];
        setBgJobs((prev) => [{ token, previewUrl: job?.thumb || null, status: 'processing' }, ...prev]);
        foregroundTokenRef.current = null;
        setIsGenerating(false);
        removePhoto();
    };

    // A live processing card clicked → that edit comes back to the photo area (the one in front, if any, swaps to the strip).
    const bringJobToForeground = (job) => {
        if (!job || job.status !== 'processing' || job.restored) return;
        const saved = jobsRef.current[job.token];
        if (!saved) return;
        if (foregroundTokenRef.current != null && isGenerating) hideCurrentGeneration();
        hiddenTokensRef.current.delete(job.token);
        foregroundTokenRef.current = job.token;
        setBgJobs((prev) => prev.filter((j) => j.token !== job.token));
        setPhoto(saved.file, saved.original);
        setGenerated(null);
        setShowResult(true);
        setIsGenerating(true);
    };
    const dismissJob = (job) => setBgJobs((prev) => prev.filter((j) => j.token !== job.token));

    // HD / Ultra HD show a one-time explanation first (unless the account turned it off), then generate.
    const onTier = (tier) => {
        const hidden = tier === 'hd' ? user?.settings?.hideHdInfo : user?.settings?.hideUltraInfo;
        if (hidden) handleGenerate(tier); else setQualityInfoTier(tier);
    };
    const canGenerate = !!file && !isGenerating && (!generated || settingsKey !== generatedKey);
    // The studio generates in HD or Ultra HD only (no Normal tier), whatever the account's AI-editor quality settings say. HD stops at
    // 4R: a bigger print only gets Ultra HD.
    const tiers = [
        hdAllowed(size) && { key: 'hd', name: t('imageGeneration.quality.hd'), cost: tierCosts?.hd, icon: <IconSparkles size={18} />, cls: 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:brightness-110' },
        { key: 'ultra', name: t('imageGeneration.quality.ultra'), cost: tierCosts?.ultra, icon: <IconCrown size={18} />, cls: 'bg-gradient-to-r from-red-600 to-rose-700 text-white hover:brightness-110' },
    ].filter(Boolean);
    const generateLabel = generated ? t('imageGeneration.quality.regeneratePrefix') : t('imageGeneration.quality.generatePrefix');

    // ── Actions on the result ────────────────────────────────────────────────────────────────────────────────────────────────
    const [printOpen, setPrintOpen] = useState(false);
    const [printTarget, setPrintTarget] = useState({ url: '', id: null, sizeMm: null });
    const [customDlOpen, setCustomDlOpen] = useState(false);
    const [customDlTarget, setCustomDlTarget] = useState({ url: '', id: null });
    const [copying, setCopying] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImage, setViewerImage] = useState(null);

    // ── The result's editor (Adjust / Filters / Crop) — never saved: the instant download, print and copy below bake it in. ────────────
    // `editSource` is the result's bytes (fetched through the backend once the editor opens — the R2 host sends no CORS headers),
    // `editCrop` a crop of it made in the crop stage (then the edits apply to the crop). Both reset with every new result.
    const [edits, setEdits] = useState(NEUTRAL_EDITS);
    const [editSource, setEditSource] = useState(null);
    const [editCrop, setEditCrop] = useState(null);
    const [editCropSrc, setEditCropSrc] = useState(null); // the crop stage, open on this object URL
    const editCropStageRef = useRef(null);
    const editSourceIdRef = useRef(null);
    useEffect(() => { setEdits(NEUTRAL_EDITS); setEditSource(null); setEditCrop(null); editSourceIdRef.current = null; }, [generated?.id]);
    const loadEditSource = useCallback(async () => {
        if (!generated || editSourceIdRef.current === generated.id) return;
        editSourceIdRef.current = generated.id;
        try {
            setEditSource(await imageGenerationService.downloadImage(generated.id));
        } catch {
            editSourceIdRef.current = null;
            notifications.show({ title: t('imageGeneration.notifications.loadFailed'), message: t('imageGeneration.notifications.loadFailedMessage'), color: 'red' });
        }
    }, [generated, t]);
    const editBase = editCrop || editSource;
    const previewUrlEdited = usePhotoEditPreview(editBase, edits);
    // a crop with no other edit still has to show — its own object URL
    const cropOnlyUrl = useMemo(() => (editCrop ? URL.createObjectURL(editCrop) : null), [editCrop]);
    useEffect(() => () => { if (cropOnlyUrl) URL.revokeObjectURL(cropOnlyUrl); }, [cropOnlyUrl]);
    const editedUrl = previewUrlEdited || cropOnlyUrl;
    const filterThumbs = useFilterThumbs(editBase);
    const hasEdits = isEdited(edits) || !!editCrop;
    // The result as it leaves the page: the crop (if any) with the edits burnt in, or the untouched file.
    const editedResultBlob = async (raw) => {
        const base = editCrop || raw || editSource || await imageGenerationService.downloadImage(generated.id);
        return isEdited(edits) ? bakePhotoEdits(base, edits) : base;
    };
    const openEditCrop = async () => {
        const base = editSource || await imageGenerationService.downloadImage(generated.id).catch(() => null);
        if (!base) return;
        if (!editSource) { setEditSource(base); editSourceIdRef.current = generated.id; }
        setEditCropSrc(URL.createObjectURL(base));
    };
    const closeEditCrop = () => { if (editCropSrc) URL.revokeObjectURL(editCropSrc); setEditCropSrc(null); };

    const downloadRecord = async (rec, withEdits = false) => {
        try {
            const raw = await imageGenerationService.downloadImage(rec.id);
            const blob = withEdits ? await editedResultBlob(raw) : raw;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const name = (rec.fileName || 'studio-photo').replace(/\.[^/.]+$/, '');
            a.href = url; a.download = `${name}.${(blob.type || '').includes('png') ? 'png' : 'jpg'}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            imageGenerationService.trackDownload(rec.id).catch(() => {});
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.downloadFailed'), message: t('imageGeneration.notifications.downloadFailedMessage'), color: 'red' });
        }
    };
    const copyResult = async () => {
        if (!generated) return;
        setCopying(true);
        try {
            // The editor's changes go along, as with the instant download and print.
            await copyImageToClipboard(hasEdits ? editedResultBlob() : imageGenerationService.downloadImage(generated.id));
            notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green' });
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.shareFailed'), message: t('imageGeneration.notifications.shareFailedMessage'), color: 'red' });
        } finally { setCopying(false); }
    };
    // The print sheet places the photo at the size it was MADE for (its record's photoSize — the file has that shape), not at the
    // size picked in the panel now, which may have changed since. `shape` = the edited picture's pixel size: a quarter turn swaps the
    // print size and a crop fits its own shape inside it.
    const openPrint = (url, id, shape = null) => {
        const photoSize = (generated?.id === id ? generated.photoSize : null) || history.find((h) => h.id === id)?.photoSize;
        let sizeMm = sizeDims(photoSize);
        if (sizeMm && shape) {
            const box = (shape.w > shape.h) === (sizeMm.w > sizeMm.h) ? sizeMm : { w: sizeMm.h, h: sizeMm.w };
            const s = Math.min(box.w / shape.w, box.h / shape.h);
            sizeMm = { w: +(shape.w * s).toFixed(1), h: +(shape.h * s).toFixed(1) };
        }
        setPrintTarget({ url, id, sizeMm });
        setPrintOpen(true);
        setViewerOpen(false);
    };
    // The result in the photo area prints with the edits burnt in (a blob: copy, dropped when the print modal closes).
    const printBlobRef = useRef(null);
    const dropPrintBlob = () => { if (printBlobRef.current) { URL.revokeObjectURL(printBlobRef.current); printBlobRef.current = null; } };
    const printResult = async () => {
        if (!generated) return;
        if (!hasEdits) { openPrint(generated.aiImage, generated.id); return; }
        try {
            const edited = await editedResultBlob();
            const bmp = await createImageBitmap(edited);
            const shape = { w: bmp.width, h: bmp.height };
            bmp.close?.();
            dropPrintBlob();
            printBlobRef.current = URL.createObjectURL(edited);
            openPrint(printBlobRef.current, generated.id, shape);
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.downloadFailed'), message: t('imageGeneration.notifications.downloadFailedMessage'), color: 'red' });
        }
    };
    useEffect(() => dropPrintBlob, []);
    const openCustomDownload = (url, id) => { setCustomDlTarget({ url, id }); setCustomDlOpen(true); };

    // ── Like / dislike the result (for internal use): one rating per photo; a dislike asks why first ──────────────────────────
    const [feedbackLoading, setFeedbackLoading] = useState(null); // 'positive' | 'negative' while saving
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const submitFeedback = async (feedback, reason = null, note = null) => {
        const id = generated?.id;
        if (!id || generated.feedback || feedbackLoading) return;
        setFeedbackLoading(feedback);
        try {
            await imageGenerationService.submitFeedback(id, feedback, reason, note);
            setGenerated((g) => (g?.id === id ? { ...g, feedback } : g));
            setShowFeedbackModal(false);
            notifications.show({ message: t('worldCupJersey.feedback.thanks'), color: 'green', autoClose: 3000 });
        } catch {
            notifications.show({ message: t('worldCupJersey.feedback.failed'), color: 'red' });
        } finally { setFeedbackLoading(null); }
    };

    // ── Reopening a saved edit (from the recent strip, or My Files → ?editId=s12) ────────────────────────────────────────────
    const [loadingEdit, setLoadingEdit] = useState(false);
    const restoreRecord = async (id) => {
        setLoadingEdit(true);
        try {
            const res = await imageGenerationService.getImageById(id);
            const rec = res?.data;
            if (!rec) throw new Error('not found');
            const originalBlob = await imageGenerationService.downloadImage(id, 'original');
            const photo = new File([originalBlob], 'studio-original.jpg', { type: originalBlob.type || 'image/jpeg' });
            const recPeople = PEOPLE.includes(rec.studio?.peopleCount) ? rec.studio.peopleCount : 'single';
            const codes = String(rec.dressCode || 'original').split('|');
            const extras = String(rec.studio?.dressColorExtra || '').split('|');
            const makeDress = (code, extra) => {
                const { type, color } = parseDress(code);
                const ok = type === 'original' || (isKnownGarment(type) && ALL_DRESS_COLORS.some((c) => c.id === color));
                return applyDressExtra({ ...DEFAULT_DRESS, value: ok ? (code || 'original') : 'original' }, extra, type);
            };

            skipSaveRef.current = true;
            setRestoreSeq((n) => n + 1);
            orientTouchedRef.current = true; // the saved print orientation was a deliberate choice
            setSize(isStudioSize(rec.photoSize) && parseSize(rec.photoSize) ? rec.photoSize : DEFAULT_SIZE);
            setPeople(recPeople);
            setBg(isValidBg(rec.background) ? rec.background : WHITE);
            setBgBlend(BG_BLENDS.includes(rec.studio?.bgBlend) ? rec.studio.bgBlend : '');
            if (recPeople === 'dual') { setDressLeft(makeDress(codes[0], extras[0])); setDressRight(makeDress(codes[1], extras[1])); }
            else if (recPeople === 'group') { setDressMen(makeDress(codes[0], extras[0])); setDressWomen(makeDress(codes[1], extras[1])); setDressChildren(makeDress(codes[2], extras[2])); }
            else setDressSingle(makeDress(codes[0], extras[0]));
            setTools(bulkToolsFromList(rec.studio?.bulkTools));
            setCustomPrompt(sanitizePhotoPrompt(rec.extraEditingGuide || ''));
            setPhoto(photo, photo);
            setGenerated({ id: rec.id, aiImage: rec.aiImage, background: rec.background, photoSize: rec.photoSize, peopleCount: recPeople, feedback: rec.studio?.feedback || null });
            setShowResult(false); // a reopened edit starts on the original photo (the result is one click away)
            setViewerOpen(false);
            notifications.show({ title: t('imageGeneration.notifications.imageLoaded'), message: t('imageGeneration.notifications.imageLoadedMessage'), color: 'blue' });
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.loadFailed'), message: t('imageGeneration.notifications.loadFailedMessage'), color: 'red' });
        } finally { setLoadingEdit(false); }
    };
    const editIdLoadedRef = useRef(false);
    useEffect(() => {
        const editId = searchParams.get('editId');
        if (!editId || editIdLoadedRef.current) return;
        editIdLoadedRef.current = true;
        setSearchParams({}, { replace: true });
        if (/^s\d+$/.test(editId)) restoreRecord(editId); // only a studio photo ("s12") opens here
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // A photo picked from the booth goes through the same crop as an upload; the crop's commit carries its booth origin along.
    const pickFromBooth = (picked, uploadId, meta) => {
        if (isGenerating) return;
        processFile(picked);
        boothPendingRef.current = { id: uploadId, isPrivate: !!meta?.isPrivate };
        setBoothMarkedIds([uploadId]);
    };

    const removePhoto = () => {
        setPhoto(null, null); setGenerated(null); setGeneratedKey(null);
        boothPhotoRef.current = null; boothPendingRef.current = null; setBoothMarkedIds([]);
    };
    const resetSettings = () => {
        orientTouchedRef.current = false;
        setSize(DEFAULT_SIZE); setPeople('single'); setBg(DEFAULT_BG); setBgBlend('');
        setDressSingle(DEFAULT_DRESS); setDressLeft(DEFAULT_DRESS); setDressRight(DEFAULT_DRESS); setDressMen(DEFAULT_DRESS); setDressWomen(DEFAULT_DRESS); setDressChildren(DEFAULT_DRESS); setCustomPrompt('');
        setTools({});
    };
    const settingsChanged = size !== DEFAULT_SIZE || people !== 'single' || bg !== DEFAULT_BG || anyDressChosen || !!customPrompt.trim()
        || Object.keys(tools).length > 0;

    // ── Layout ───────────────────────────────────────────────────────────────────────────────────────────────────────────────
    // The controls are an icon rail + ONE panel (like Canva): the rail picks the section, the panel shows only that section.
    // The dress section starts with how many people are in the photo; a group keeps its clothes, so it has no dress picker.
    const pickSize = (key) => setSize(key);
    const pickOrientation = (isLandscape) => { orientTouchedRef.current = true; setSize(sizeKey(parseSize(size)?.id || '4r', isLandscape)); };
    const toolCount = Object.keys(tools).length;
    const sections = [
        boothOn && { key: 'booth', Icon: IconDeviceMobileMessage, label: t('photoCollectorPanel.title'), title: t('photoCollectorPanel.title'), desc: t('studioEditor.panelDesc_booth') },
        { key: 'size',  Icon: IconRuler2, label: t('studioEditor.rail_size'),  title: t('imageGeneration.photoSize'),         desc: t('studioEditor.chooseSizeDesc'), value: parseSize(size)?.def?.label },
        { key: 'bg',    Icon: IconPhoto,  label: t('studioEditor.rail_bg'),    title: t('imageGeneration.background'),        desc: t('studioEditor.panelDesc_bg'), dot: bg !== DEFAULT_BG },
        { key: 'dress', Icon: IconShirt,  label: t('studioEditor.rail_dress'), title: t('imageGeneration.clothingStyle'),     desc: t('studioEditor.panelDesc_dress'), dot: anyDressChosen || people !== 'single' },
        { key: 'tools', Icon: IconWand,   label: t('studioEditor.rail_tools'), title: t('imageGeneration.extraEditingGuide'), desc: t('studioEditor.panelDesc_tools'), count: toolCount },
        { key: 'prompt', Icon: IconMessage2, label: t('studioEditor.rail_prompt'), title: t('imageGeneration.enableCustomInstruction'), desc: t('studioEditor.panelDesc_prompt'), dot: !!customPrompt.trim() },
    ].filter(Boolean);
    const current = sections.find((x) => x.key === section) || sections[0];
    const floatingOpen = isCompact && panelOpen;
    useEffect(() => {
        if (!floatingOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPanelOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [floatingOpen]);
    useEffect(() => { if (!isCompact) setPanelOpen(false); }, [isCompact]);
    // The floating panel ends 8px above the bottom nav (or the screen edge from lg), wherever its top lands (re-measured on scroll / resize).
    const floatPanelRef = useRef(null);
    const [floatMaxH, setFloatMaxH] = useState(null);
    useLayoutEffect(() => {
        if (!floatingOpen) return undefined;
        const measure = () => {
            const el = floatPanelRef.current;
            if (!el) return;
            const nav = window.innerWidth < 1024 ? 52 : 0; // the bottom nav exists only below lg
            setFloatMaxH(Math.max(240, Math.floor(window.innerHeight - el.getBoundingClientRect().top - nav - 8)));
        };
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
    }, [floatingOpen, section]);
    const pickRail = (key) => {
        if (isCompact && panelOpen && key === current.key) { setPanelOpen(false); return; }
        setSection(key);
        if (isCompact) setPanelOpen(true);
    };
    const next = sections[sections.indexOf(current) + 1];
    const prev = sections[sections.indexOf(current) - 1];
    const stepBtnCls = 'flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-600 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-zinc-700 dark:text-zinc-300';

    const panelBody = {
        size: (
            <div className="space-y-3">
                <OrientationToggle landscape={!!parseSize(size)?.landscape} disabled={isGenerating} onChange={pickOrientation} />
                <SizeOptions size={size} disabled={isGenerating} onSelect={pickSize} />
            </div>
        ),
        bg: <BackgroundPicker bg={bg} onPick={setBg} blend={bgBlend} onBlendChange={setBgBlend} disabled={isGenerating} />,
        dress: (
            <StudioDressSection
                people={people}
                dresses={dresses}
                onPeopleChange={setPeople}
                onDressChange={(key, d) => setDressFor[key](d)}
                disabled={isGenerating}
            />
        ),
        tools: <StudioToolsPanel value={tools} onChange={setTools} busy={isGenerating} />,
        prompt: <CustomPromptField value={customPrompt} onChange={setCustomPrompt} disabled={isGenerating} />,
    };

    // Generate — HD and Ultra HD (the studio has no Normal tier; Ultra HD alone above 4R). From `xl` the panel's footer, stacked; below it a
    // bar under the photo, side by side, that docks above the 52px bottom nav while the page scrolls.
    const generateBar = (
        <div className={cn('sticky bottom-[52px] z-20 grid lg:bottom-3 gap-2 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 xl:static xl:flex xl:flex-col xl:rounded-none xl:rounded-b-2xl xl:border-0 xl:border-t xl:border-zinc-100 xl:p-4 xl:shadow-none xl:dark:border-zinc-800', tiers.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
            {tiers.map((ti) => (
                <div key={ti.key} className="relative">
                    <button
                        type="button"
                        disabled={!canGenerate}
                        onClick={() => onTier(ti.key)}
                        className={cn('inline-flex h-full w-full items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-sm font-bold leading-tight transition disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-4 sm:text-[15px] xl:py-3', ti.cls)}
                    >
                        {ti.icon}
                        {`${generateLabel} · ${ti.name}`}
                    </button>
                    {ti.cost != null && (
                        <span className="absolute -right-1.5 -top-2 inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-xs font-bold text-zinc-800 shadow dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
                            {fmtNum(ti.cost)} <IconCoins size={13} className="text-primary" />
                        </span>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="py-4 font-hind sm:py-6">
            {user?.role === 'OWNER' && <LowCreditBanner credit={effectiveCredit} className="pb-3" />}
            {/* Narrow screens: a tap outside the floating panel closes it */}
            {floatingOpen && <div className="fixed inset-0 z-30 bg-black/20 animate-in fade-in-0 duration-200" onClick={() => setPanelOpen(false)} aria-hidden="true" />}
            {/* rows `auto 1fr`: the left column spans both rows and is often the taller one — its extra height goes to the second row, so
                the recent strip sits right under the photo instead of the grid spreading a gap between them */}
            <div className="grid grid-cols-[64px_minmax(0,1fr)] grid-rows-[auto_1fr] items-start gap-3 xl:grid-cols-[400px_minmax(0,1fr)] xl:gap-4">
                {/* ── LEFT: the controls (below `lg` just the rail; the panel floats out beside it) ── */}
                <aside className={cn('sticky top-3 col-start-1 row-span-2 row-start-1 flex flex-col rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900 xl:top-4 xl:h-[calc(100vh-8rem)] xl:min-h-[560px] xl:overflow-hidden', floatingOpen && 'z-40')}>
                    <div className="flex min-h-0 flex-1 flex-col xl:flex-row">
                        {/* Rail — a slim column at every width */}
                        <nav className="flex shrink-0 flex-col gap-1 rounded-2xl border-zinc-200 bg-zinc-100 p-1 dark:border-zinc-800 dark:bg-zinc-950 xl:w-[76px] xl:overflow-y-auto xl:rounded-none xl:rounded-tl-2xl xl:border-r xl:p-1.5 xl:py-2" aria-label={t('studioEditor.title')}>
                            {sections.map((x) => {
                                const active = x.key === current.key;
                                return (
                                    <button
                                        key={x.key}
                                        type="button"
                                        onClick={() => pickRail(x.key)}
                                        aria-current={active ? 'page' : undefined}
                                        aria-expanded={isCompact ? floatingOpen && active : undefined}
                                        className={cn(
                                            'group relative flex w-full flex-col items-center gap-1 rounded-xl px-0.5 py-2 transition-colors',
                                            active ? 'text-primary' : 'text-zinc-500 hover:bg-white hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
                                        )}
                                    >
                                        {active && <span className="absolute -left-1 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-full bg-primary xl:-left-1.5" />}
                                        <span className={cn('relative flex h-9 w-9 items-center justify-center rounded-xl transition-all', active ? 'bg-primary text-white shadow-md shadow-primary/30' : 'bg-white ring-1 ring-zinc-200 group-hover:ring-zinc-300 dark:bg-zinc-800 dark:ring-zinc-700')}>
                                            <x.Icon size={18} strokeWidth={1.8} />
                                            {x.count > 0 && (
                                                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-zinc-900">{fmtNum(x.count)}</span>
                                            )}
                                            {x.dot && !x.count && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />}
                                        </span>
                                        <span className="w-full truncate text-center text-[10.5px] font-semibold leading-tight">{x.label}</span>
                                        {x.value && <span className="-mt-0.5 text-[10px] font-bold leading-none text-primary">{x.value}</span>}
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Panel — only the chosen section. Below `xl` a floating card beside the rail, shown while a tab is open. */}
                        <div
                            ref={floatPanelRef}
                            style={floatingOpen && floatMaxH ? { maxHeight: floatMaxH } : undefined}
                            className={cn(
                                'flex min-h-0 min-w-0 flex-1 flex-col',
                                'max-xl:absolute max-xl:left-full max-xl:top-0 max-xl:ml-2 max-xl:max-h-[calc(100vh-9rem)] max-xl:w-[min(360px,calc(100vw-6.5rem))] max-xl:rounded-2xl max-xl:border max-xl:border-zinc-200 max-xl:bg-white max-xl:shadow-2xl max-xl:dark:border-zinc-700 max-xl:dark:bg-zinc-900',
                                floatingOpen ? 'max-xl:animate-in max-xl:fade-in-0 max-xl:slide-in-from-left-2 max-xl:duration-200' : 'max-xl:hidden',
                            )}
                        >
                            <div className="flex items-start gap-2 px-4 pb-2 pt-3.5">
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{current.title}</h2>
                                    <p className="mt-0.5 text-xs leading-snug text-zinc-500 dark:text-zinc-400">{current.desc}</p>
                                </div>
                                {settingsChanged && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={resetSettings} disabled={isGenerating} className="shrink-0 rounded-lg p-1.5 text-zinc-400 hover:bg-primary/10 hover:text-primary disabled:opacity-50" aria-label={t('studioEditor.resetAll')}>
                                                <IconRotate size={17} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="z-[2000]">{t('studioEditor.resetAll')}</TooltipContent>
                                    </Tooltip>
                                )}
                                <button type="button" onClick={() => setPanelOpen(false)} className="shrink-0 rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 xl:hidden" aria-label={t('common.close')}>
                                    <IconX size={17} />
                                </button>
                            </div>
                            {/* The booth inbox stays mounted (hidden on the other sections) so its on/off status is known for the rail */}
                            <div className={cn('min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1', current.key !== 'booth' && 'hidden')}>
                                <PhotoBoothInbox onPickFile={pickFromBooth} markedIds={boothMarkedIds} refreshKey={boothRefresh} onStatusChange={setBoothOn} />
                            </div>
                            <div key={current.key} className={cn('min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-200', current.key === 'booth' && 'hidden')}>
                                {panelBody[current.key]}
                                {/* Step back / forward through the sections */}
                                {(prev || next) && (
                                    <div className="mt-4 grid grid-cols-2 gap-2">
                                        {prev ? (
                                            <button type="button" onClick={() => setSection(prev.key)} className={stepBtnCls}>
                                                <IconArrowLeft size={15} className="shrink-0" />
                                                <span className="min-w-0 text-left">
                                                    <span className="block text-[10px] font-medium text-zinc-400">{t('studioEditor.prevStep')}</span>
                                                    <span className="block truncate">{prev.label}</span>
                                                </span>
                                            </button>
                                        ) : <span />}
                                        {next && (
                                            <button type="button" onClick={() => setSection(next.key)} className={cn(stepBtnCls, 'justify-end')}>
                                                <span className="min-w-0 text-right">
                                                    <span className="block text-[10px] font-medium text-zinc-400">{t('studioEditor.nextStepShort')}</span>
                                                    <span className="block truncate">{next.label}</span>
                                                </span>
                                                <IconArrowRight size={15} className="shrink-0" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {!isCompact && generateBar}
                </aside>

                {/* ── RIGHT: the photo ── */}
                <main className={cn('col-start-2 row-start-1 min-w-0 space-y-3', loadingEdit && 'pointer-events-none opacity-60')}>
                    <StudioPhotoArea
                        size={size}
                        onHide={hideCurrentGeneration}
                        onSizeClick={isGenerating ? undefined : () => setSizeDialogOpen(true)}
                        onPreview={() => { setViewerImage(history.find((h) => h.id === generated?.id) || generated); setViewerOpen(true); }}
                        previewUrl={previewUrl}
                        resultUrl={generated?.aiImage || null}
                        resultSize={generated?.photoSize || null}
                        showResult={showResult}
                        onShowResult={setShowResult}
                        isGenerating={isGenerating}
                        edits={edits}
                        onEditsChange={setEdits}
                        editedUrl={editedUrl}
                        filterThumbs={filterThumbs}
                        editCropped={!!editCrop}
                        onEditCrop={openEditCrop}
                        onClearEditCrop={() => setEditCrop(null)}
                        onEditorOpen={loadEditSource}
                        onPickFile={processFile}
                        onRemove={removePhoto}
                        onRecrop={() => { const origin = boothPhotoRef.current; processFile(originalFile); boothPendingRef.current = origin; }}
                        onDownload={() => generated && downloadRecord({ id: generated.id, fileName: history.find((h) => h.id === generated.id)?.fileName }, hasEdits)}
                        onCustomDownload={() => generated && openCustomDownload(generated.aiImage, generated.id)}
                        onPrint={printResult}
                        onCopy={copyResult}
                        copying={copying}
                        rated={!!generated?.feedback}
                        feedbackLoading={feedbackLoading}
                        onLike={() => submitFeedback('positive')}
                        onDislike={() => setShowFeedbackModal(true)}
                        isDragging={isDragging}
                        dragHandlers={dragHandlers}
                        inputRef={fileInputRef}
                    />
                    {isCompact && generateBar}
                </main>

                {/* ── RECENT: under the photo ── */}
                <section className={cn('col-start-2 row-start-2 min-w-0', loadingEdit && 'pointer-events-none opacity-60')}>
                    <StudioRecentPhotos
                        history={history}
                        bgJobs={bgJobs}
                        onOpenJob={bringJobToForeground}
                        onDismissJob={dismissJob}
                        historyLoading={historyLoading}
                        onOpenHistory={(img) => { setViewerImage(img); setViewerOpen(true); }}
                    />
                </section>
            </div>

            {/* Crop — full screen, in the shape of the chosen print */}
            {cropSrc && (
                <div className="fixed inset-0 z-[400] flex flex-col bg-white font-hind dark:bg-zinc-900">
                    <div className="flex min-h-0 flex-1 flex-col">
                        <CropStage
                            ref={cropStageRef}
                            imageSrc={cropSrc}
                            fileName={pendingCropFile?.name}
                            isMobile={isMobile}
                            compact={false}
                            t={t}
                            aspect={cropRatio}
                            outWidth={cropOut.w}
                            outHeight={cropOut.h}
                            hint={t('studioEditor.cropHint')}
                            showSkip
                            defaultFreeCrop
                            onClose={() => cropStageRef.current?.apply()}
                            onApply={onCropApply}
                            onSkip={onCropSkip}
                            onError={onCropError}
                        />
                    </div>
                </div>
            )}

            <AiTemplateFeedbackModal
                open={showFeedbackModal}
                onOpenChange={setShowFeedbackModal}
                onSubmit={(reason, note) => submitFeedback('negative', reason, note)}
                submitting={feedbackLoading === 'negative'}
                reasons={IMAGE_FEEDBACK_REASONS}
                maxWords={10}
                strict
            />

            {/* The result's crop (edit drawer → Crop) — full resolution, free shape; it only feeds the instant download / print / copy */}
            {editCropSrc && (
                <div className="fixed inset-0 z-[400] flex flex-col bg-white font-hind dark:bg-zinc-900">
                    <div className="flex min-h-0 flex-1 flex-col">
                        <CropStage
                            ref={editCropStageRef}
                            imageSrc={editCropSrc}
                            isMobile={isMobile}
                            compact={false}
                            t={t}
                            hint=""
                            defaultFreeCrop
                            outputMode="blobFull"
                            onClose={closeEditCrop}
                            onApply={(blob) => { setEditCrop(blob); closeEditCrop(); }}
                            onError={() => { closeEditCrop(); notifications.show({ title: t('imageGeneration.notifications.fileReadError'), message: t('imageGeneration.notifications.fileReadErrorMessage'), color: 'red' }); }}
                        />
                    </div>
                </div>
            )}

            {/* A recent photo, full size — download / print / custom size / delete live in the viewer; "edit" reopens the settings */}
            <ImagePreviewModal
                opened={viewerOpen}
                onClose={() => setViewerOpen(false)}
                images={history}
                initialImage={viewerImage}
                hasMore={hasMoreHistory}
                isLoadingMore={historyLoading}
                onLoadMore={() => loadHistory(historyPage + 1, true)}
                isLoadingEdit={loadingEdit}
                onEdit={(img) => restoreRecord(img.id)}
                onImageDeleted={(deletedId) => {
                    setHistory((prev) => prev.filter((i) => i.id !== deletedId));
                    if (generated?.id === deletedId) { setGenerated(null); setGeneratedKey(null); }
                    setViewerOpen(false);
                }}
                onPrint={openPrint}
                onCustomDownload={openCustomDownload}
            />
            <PrintModal
                opened={printOpen}
                onClose={() => { setPrintOpen(false); setPrintTarget({ url: '', id: null, sizeMm: null }); dropPrintBlob(); }}
                imageUrl={printTarget.url}
                imageId={printTarget.id}
                initialSizeType="free"
                initialSizeMm={printTarget.sizeMm}
            />
            {customDlOpen && (
                <CustomDownloadModal opened={customDlOpen} onClose={() => setCustomDlOpen(false)} imageUrl={customDlTarget.url} imageId={customDlTarget.id} />
            )}
            <LimitReachedModal opened={noCreditOpen} onClose={() => setNoCreditOpen(false)} isOperator={isOperator} />

            {/* HD / Ultra HD — a one-time explanation before the first generation of that tier (the shared box the AI editor shows too) */}
            <QualityInfoDialog tier={qualityInfoTier} onClose={() => setQualityInfoTier(null)} onProceed={handleGenerate} />
            <SizeDialog open={sizeDialogOpen} onOpenChange={setSizeDialogOpen} size={size} onSelect={pickSize} onOrientation={pickOrientation} />
        </div>
    );
}
